import { z } from 'zod'
import { API_V2_BASE } from './base'
import { getRefreshToken, useAuthStore, type SessionMe } from '../stores/auth'

/** 认证相关错误：status 对应后端 HTTP 状态（401 凭证错误 / 423 锁定 / 403 禁用 / 429 限流） */
export class AuthError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
  }
}

const accountSchema = z.object({
  userId: z.string(),
  email: z.string().optional(),
  displayName: z.string().optional(),
})

const authPayloadSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
  tokenType: z.string().default('Bearer'),
  expiresIn: z.number().default(600),
  account: accountSchema,
})

export type AuthPayload = z.infer<typeof authPayloadSchema>

const envelopeSchema = z.object({
  code: z.number(),
  message: z.string(),
  data: authPayloadSchema.optional(),
})

function accountToMe(account: z.infer<typeof accountSchema>): SessionMe {
  return {
    userId: account.userId,
    email: account.email,
    displayName: account.displayName || account.email || account.userId,
  }
}

/** 认证端点统一走裸 fetch（不走 client 的 401 自动续期逻辑，避免递归） */
async function authRequest(path: string, body: unknown): Promise<AuthPayload> {
  let response: Response
  try {
    response = await fetch(`${API_V2_BASE}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch {
    throw new AuthError(0, '无法连接后端服务，请检查网络')
  }
  const json: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const message =
      json && typeof json === 'object' && 'message' in json && typeof json.message === 'string'
        ? json.message
        : `认证失败（${response.status}）`
    throw new AuthError(response.status, message)
  }
  const parsed = envelopeSchema.safeParse(json)
  if (!parsed.success || parsed.data.code !== 0 || !parsed.data.data) {
    const message = parsed.success ? parsed.data.message || '认证失败' : '认证响应格式异常'
    throw new AuthError(response.status, message)
  }
  return parsed.data.data
}

function commitSession(payload: AuthPayload): void {
  const me = useAuthStore.getState().me
  useAuthStore.getState().setSession(payload.accessToken, payload.refreshToken, me ?? accountToMe(payload.account))
}

export async function login(email: string, password: string): Promise<void> {
  const payload = await authRequest('/auth/login', { email, password })
  commitSession(payload)
}

export async function register(email: string, password: string, displayName: string): Promise<void> {
  const payload = await authRequest('/auth/register', { email, password, displayName })
  commitSession(payload)
}


/** MQTT 凭证（登录用户动态下发；EMQX 关闭匿名后 Mirror 的实时通道凭证来源） */
export async function fetchMqttCredentials(): Promise<{ username: string; password: string }> {
  const accessToken = useAuthStore.getState().token
  if (!accessToken) throw new AuthError(401, '未登录，无法获取 MQTT 凭证')
  let response: Response
  try {
    response = await fetch(`${API_V2_BASE}/auth/mqtt-credentials`, {
      method: 'POST',
      headers: { Accept: 'application/json', Authorization: `Bearer ${accessToken}` },
    })
  } catch {
    throw new AuthError(0, '无法连接后端服务，请检查网络')
  }
  const json: unknown = await response.json().catch(() => null)
  if (!response.ok || (json && typeof json === 'object' && 'code' in json && (json as {code:number}).code !== 0)) {
    throw new AuthError(response.status, 'MQTT 凭证获取失败')
  }
  const data = (json as { data?: { username?: string; password?: string } })?.data
  if (!data?.username || !data?.password) throw new AuthError(response.status, 'MQTT 凭证格式异常')
  return { username: data.username, password: data.password }
}

export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken()
  if (refreshToken) {
    try {
      await authRequest('/auth/logout', { refreshToken })
    } catch {
      /* 服务端登出失败不阻塞本地清理 */
    }
  }
  useAuthStore.getState().clear()
}

let refreshPromise: Promise<string | null> | null = null

/**
 * 静默续期（单飞）：并发调用共享同一次 refresh 请求。
 * 成功返回新 access token；失败（无效/重用/网络错误）清空会话并返回 null。
 */
export function refreshSession(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

async function doRefresh(): Promise<string | null> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    useAuthStore.getState().clear()
    return null
  }
  try {
    const payload = await authRequest('/auth/refresh', { refreshToken })
    const me = useAuthStore.getState().me ?? accountToMe(payload.account)
    useAuthStore.getState().setSession(payload.accessToken, payload.refreshToken, me)
    return payload.accessToken
  } catch {
    useAuthStore.getState().clear()
    return null
  }
}

/** 应用启动时恢复会话：内存无 token 但存在 refreshToken 时静默续期一次 */
export async function bootstrapSession(): Promise<void> {
  if (useAuthStore.getState().token) return
  if (!getRefreshToken()) return
  await refreshSession()
}
